import type { ReactNode } from 'react';
import { CheckCircle, XCircle } from 'lucide-react';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

export type SegmentationWorkflowOverlayVariant = 'loading' | 'success' | 'error';

interface SegmentationWorkflowOverlayProps {
  open: boolean;
  eyebrow?: string;
  title: string;
  message?: string;
  variant: SegmentationWorkflowOverlayVariant;
  footer?: ReactNode;
}

export function SegmentationWorkflowOverlay({
  open,
  eyebrow,
  title,
  message,
  variant,
  footer,
}: SegmentationWorkflowOverlayProps) {
  if (!open) return null;

  return (
    <div className="pointer-events-auto fixed inset-0 z-30 flex items-center justify-center bg-black/20 p-6 backdrop-blur-sm">
      <div className="flex w-full max-w-xl flex-col items-center gap-6 rounded-3xl border border-primary/40 bg-content/90 p-10 text-white shadow-2xl shadow-primary/30">
        {eyebrow && (
          <p className="text-xs font-semibold uppercase tracking-[0.35em] text-primary/70">{eyebrow}</p>
        )}

        {variant === 'loading' && (
          <>
            <h2 className="text-center text-2xl font-semibold tracking-tight">{title}</h2>
            <LoadingSpinner className="text-white" size={72} glowIntensity="md" />
            {message && <p className="text-center text-sm text-white/70">{message}…</p>}
          </>
        )}

        {variant === 'success' && (
          <>
            <CheckCircle className="h-16 w-16 text-green-400" />
            <h2 className="text-center text-2xl font-semibold tracking-tight">{title}</h2>
            {message && <p className="text-center text-sm text-white/70">{message}</p>}
          </>
        )}

        {variant === 'error' && (
          <>
            <XCircle className="h-16 w-16 text-red-400" />
            <h2 className="text-center text-2xl font-semibold tracking-tight">{title}</h2>
            {message && <p className="text-center text-sm text-white/70">{message}</p>}
            {footer}
          </>
        )}
      </div>
    </div>
  );
}
